import { useCallback, useState } from "react"
import { SortConfig } from "@hydrologiq/shared/types/pagination"
// @ts-ignore - Will be available after GraphQL codegen
import { GetAlertsDocument, GetAlertsQuery, GetAlertsQueryVariables } from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { DEFAULT_PAGE_SIZE } from "@hydrologiq/shared/config"
/**
 * Hook for fetching paginated alerts with filtering and sorting
 *
 * Provides pagination, sorting, and filtering functionality.
 * Headlines are included in the response for dashboard metrics.
 */
export function useAlertsPaginated() {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sort, setSort] = useState<SortConfig | null>(null)
  const [filters, setFilters] = useState<{
    status?: string
    severity?: string
    type?: string
    source?: string
    installation?: string
    installation_name?: string
    customer?: string
    site?: string
    asset?: string
    system?: string
  }>({})

  // Apollo query with current parameters
  const { data, loading, error, refetch } = useQueryWithEndpoint<GetAlertsQuery, GetAlertsQueryVariables>(
    GetAlertsDocument,
    {
      variables: {
        page: currentPage,
        per_page: pageSize,
        status: filters.status || null,
        severity: filters.severity || null,
        type: filters.type || null,
        source: filters.source || null,
        installation: filters.installation || null,
        installation_name: filters.installation_name || null,
        customer: filters.customer || null,
        site: filters.site || null,
        asset: filters.asset || null,
        system: filters.system || null,
        sort: sort ? `${sort.field}:${sort.direction}` : null,
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    },
    "primary"
  )

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  // Handle page size change
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size)
    setCurrentPage(1) // Reset to first page
  }, [])

  // Handle sort change
  const handleSortChange = useCallback((newSort: SortConfig | null) => {
    setSort(newSort)
    setCurrentPage(1) // Reset to first page when sorting changes
  }, [])

  // Handle filters change
  const handleFiltersChange = useCallback((newFilters: typeof filters) => {
    setFilters(newFilters)
    setCurrentPage(1) // Reset to first page when filters change
  }, [])

  // Clear all filters and sorting
  const handleClearAll = useCallback(() => {
    setFilters({})
    setSort(null)
    setCurrentPage(1)
  }, [])

  // Handle refetch
  const handleRefetch = useCallback(() => {
    refetch()
  }, [refetch])

  return {
    alerts: data?.getAlerts?.data || [],
    pagination: data?.getAlerts?.meta,
    headlines: data?.getAlerts?.headlines,
    filterOptions: data?.getAlerts?.filter_options,
    loading,
    error,
    filters,
    sort,
    fetchPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    setSort: handleSortChange,
    setFilters: handleFiltersChange,
    clearAll: handleClearAll,
    refetch: handleRefetch,
  }
}
